import { Routes } from '@angular/router';
import { SUPER_ADMIN_PERMISSION_SLUGS as P } from 'src/app/core/constants/super-admin-permission-slugs';
import { permissionGuard } from 'src/app/core/guards/permission.guard';
import { PagesComponent } from './pages.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { PlatformHealthComponent } from './platform-health/platform-health.component';
import { ProfileComponent } from '../users/profile';
import { MailboxComponent } from '../apps/mailbox';
import { CalendarComponent } from '../apps/calendar';
import { UserAccountSettingsComponent } from '../users/user-account-settings';
import { ChatComponent } from '../apps/chat';
import { ManageAgentComponent } from './manage-agent/manage-agent.component';
import { AgentDetailComponent } from './manage-agent/agent-detail/agent-detail.component';
import { ManageClientComponent } from './manage-client/manage-client.component';
import { CreateClientComponent } from './manage-client/create-client/create-client.component';
import { EditClientComponent } from './manage-client/edit-client/edit-client.component';
import { ClientDetailComponent } from './manage-client/client-detail/client-detail.component';
import { AuditTrailComponent } from './audit-trail/audit-trail.component';
import { SessionDetailComponent } from './audit-trail/session-detail/session-detail.component';
import { AuditLogComponent } from './audit-log/audit-log.component';
import { ReportsComponent } from './reports/reports.component';
import { ReportDetailComponent } from './reports/report-detail/report-detail.component';
import { UserSettingComponent } from './user-setting/user-setting.component';
import { EditEmailTemplateComponent } from './user-setting/edit-email-template/edit-email-template.component';
import { ManageIntegrationsComponent } from './manage-integrations/manage-integrations.component';
import { BillingComponent } from './billing/billing.component';
import { PaymentsComponent } from './billing/payments/payments.component';
import { InvoiceDashboardComponent } from './billing/invoice-dashboard/invoice-dashboard.component';
import { invoicesByBusinessRedirect } from './billing/billing-redirects';
import { RecordPaymentComponent } from './billing/record-payment/record-payment.component';
import { RulePacksComponent } from './rule-packs/rule-packs.component';
import { RulePacksEditComponent } from './rule-packs/rule-packs-edit/rule-packs-edit.component';
import { CountryPacksComponent } from './country-packs/country-packs.component';
import { DocumentTypesComponent } from './document-types/document-types.component';
import { ProductsComponent } from './products/products.component';
import { ProductDocumentsComponent } from './products/product-documents.component';
import { ComplianceLocksComponent } from './compliance-locks/compliance-locks.component';
import { PromptTemplatesComponent } from './prompt-templates/prompt-templates.component';
import { SystemPreferencesComponent } from './system-preferences/system-preferences.component';
import { ManageRolePermissionsComponent } from './manage-roles/manage-role-permissions/manage-role-permissions.component';
import { ManageAdminUsersComponent } from './manage-admin-users/manage-admin-users.component';
import { CreateAdminUserComponent } from './manage-admin-users/create-admin-user/create-admin-user.component';
import { AdminUserDetailComponent } from './manage-admin-users/admin-user-detail/admin-user-detail.component';
import { NotificationsComponent } from './notifications/notifications.component';
import { ActivitiesComponent } from './activities/activities.component';
import { ManageSubscriptionsComponent } from './manage-subscriptions/manage-subscriptions.component';
import { LlmModelsComponent } from './llm-models/llm-models.component';
import { LlmConfigComponent } from './llm-config/llm-config.component';
import { LlmCostComponent } from './llm-cost/llm-cost.component';
import { LlmUsageComponent } from './llm-usage/llm-usage.component';
import { LlmPdCostComponent } from './llm-pd-cost/llm-pd-cost.component';
import { LlmPricingComponent } from './llm-pricing/llm-pricing.component';
import { LlmTenantCostComponent } from './llm-tenant-cost/llm-tenant-cost.component';

/** Route guard config: user needs any listed permission slug (unless `requireAll` is set). */
function withPermission(
  permissions: string | string[],
  fallbackRoute = '/dashboard',
): Pick<Routes[number], 'canActivate' | 'data'> {
  return {
    canActivate: [permissionGuard],
    data: { permissions, fallbackRoute },
  };
}

/** Registered under `AppLayout` in `app.route.ts` (eager) so session `canActivate` always runs before any page. */
export const PAGE_ROUTES: Routes = [
  {
    path: '',
    component: PagesComponent,
    children: [
      {
        path: 'dashboard',
        component: DashboardComponent,
        ...withPermission([P.DASHBOARD_VIEW]),
      },
      {
        path: 'platform-health',
        component: PlatformHealthComponent,
        ...withPermission([P.DASHBOARD_VIEW]),
      },
      { path: 'notifications', component: NotificationsComponent },
      { path: 'activities', component: ActivitiesComponent },

      {
        path: 'manage-agent/:uuid',
        component: AgentDetailComponent,
        ...withPermission([P.AGENT_VIEW], '/manage-agent'),
      },
      {
        path: 'manage-agent',
        component: ManageAgentComponent,
        ...withPermission([P.AGENT_LIST, P.AGENT_ARCHIVED_LIST]),
      },
      {
        path: 'create-agent',
        loadChildren: () =>
          import('./manage-agent/create-agent/create-agent.routes').then((m) => m.CREATE_AGENT_ROUTES),
        ...withPermission([P.AGENT_ADD, P.AGENT_UPDATE], '/manage-agent'),
      },

      {
        path: 'manage-client/create-client',
        component: CreateClientComponent,
        ...withPermission(P.TENANT_ADD, '/manage-client'),
      },
      {
        path: 'manage-client/:uuid/edit',
        component: EditClientComponent,
        ...withPermission(P.TENANT_UPDATE, '/manage-client'),
      },
      {
        path: 'manage-client/:uuid',
        component: ClientDetailComponent,
        ...withPermission([P.TENANT_VIEW], '/manage-client'),
      },
      {
        path: 'manage-client',
        component: ManageClientComponent,
        ...withPermission([P.TENANT_LIST, P.TENANT_ARCHIVED_LIST]),
      },

      {
        path: 'manage-admin-users/:uuid/edit',
        component: CreateAdminUserComponent,
        ...withPermission(P.USER_UPDATE, '/manage-admin-users'),
      },
      {
        path: 'manage-admin-users/:uuid',
        component: AdminUserDetailComponent,
        ...withPermission([P.USER_VIEW], '/manage-admin-users'),
      },
      {
        path: 'manage-admin-users',
        component: ManageAdminUsersComponent,
        ...withPermission([P.USER_LIST, P.USER_ARCHIVED_LIST]),
      },
      {
        path: 'create-admin-user',
        component: CreateAdminUserComponent,
        ...withPermission(P.USER_ADD, '/manage-admin-users'),
      },
      { path: 'admin-user-detail/:id', redirectTo: 'manage-admin-users' },
      { path: 'edit-admin-user/:id', redirectTo: 'manage-admin-users' },

      { path: 'rule-packs', component: RulePacksComponent, ...withPermission([P.RULE_PACK_LIST]) },
      { path: 'rule-packs/edit', component: RulePacksEditComponent, ...withPermission([P.RULE_PACK_LIST]) },
      { path: 'country-packs', component: CountryPacksComponent, ...withPermission([P.RULE_PACK_LIST]) },
      {
        path: 'document-types',
        component: DocumentTypesComponent,
        ...withPermission([P.RULE_PACK_LIST]),
      },
      {
        path: 'products',
        component: ProductsComponent,
        ...withPermission([P.PRODUCT_DOCUMENT_LIST]),
      },
      {
        path: 'products/:slug/documents',
        component: ProductDocumentsComponent,
        ...withPermission([P.PRODUCT_DOCUMENT_LIST]),
      },
      {
        path: 'workflows',
        redirectTo: 'workflows/global',
        pathMatch: 'full',
      },
      {
        path: 'workflows/:mode',
        // Lazy: the diagram library is ~400kB and belongs on the one page that draws.
        loadComponent: () =>
          import('./workflows/workflows.component').then((m) => m.WorkflowsComponent),
        canDeactivate: [
          (component: { canLeave: () => boolean }): boolean => component.canLeave(),
        ],
        ...withPermission([P.WORKFLOW_LIST]),
      },
      {
        path: 'compliance-locks',
        component: ComplianceLocksComponent,
        ...withPermission([P.RULE_PACK_LIST]),
      },
      {
        path: 'prompt-templates',
        component: PromptTemplatesComponent,
        ...withPermission([P.RULE_PACK_LIST]),
      },
      { path: 'llm-models', component: LlmModelsComponent, ...withPermission([P.LLM_CONFIG_MANAGE]) },
      { path: 'llm-config', component: LlmConfigComponent, ...withPermission([P.LLM_CONFIG_MANAGE]) },
      { path: 'llm-cost', component: LlmCostComponent, ...withPermission([P.LLM_COST_VIEW]) },
      { path: 'llm-usage', component: LlmUsageComponent, ...withPermission([P.LLM_COST_VIEW]) },
      { path: 'llm-pd-cost', component: LlmPdCostComponent, ...withPermission([P.LLM_COST_VIEW]) },
      { path: 'llm-tenant/:tenantId', component: LlmTenantCostComponent, ...withPermission([P.LLM_COST_VIEW]) },
      { path: 'llm-pricing', component: LlmPricingComponent, ...withPermission([P.LLM_PRICING_MANAGE]) },
      { path: 'integrations', component: ManageIntegrationsComponent, ...withPermission([P.INTEGRATION_LIST]) },
      { path: 'billing', component: BillingComponent, ...withPermission([P.BILLING_VIEW]) },
      { path: 'billing/payments', component: PaymentsComponent, ...withPermission([P.BILLING_VIEW]) },
      { path: 'invoices', component: InvoiceDashboardComponent, ...withPermission([P.BILLING_VIEW]) },
      { path: 'invoices/business/:tenantId', redirectTo: invoicesByBusinessRedirect },
      { path: 'invoices/payment/:invoiceId', component: RecordPaymentComponent, ...withPermission([P.BILLING_MANAGE]) },
      {
        path: 'subscriptions',
        component: ManageSubscriptionsComponent,
        ...withPermission([P.SUBSCRIPTION_LIST, P.SUBSCRIPTION_ARCHIVED_LIST]),
      },
      {
        path: 'audit-trail/session/:id',
        component: SessionDetailComponent,
        ...withPermission([P.AUDIT_TRAIL_LIST, P.AUDIT_TRAIL_VIEW], '/audit-trail'),
      },
      {
        path: 'audit-trail',
        component: AuditTrailComponent,
        ...withPermission([P.AUDIT_TRAIL_LIST, P.AUDIT_TRAIL_VIEW]),
      },
      { path: 'audit-logs', redirectTo: 'audit-trail', pathMatch: 'full' },
      { path: 'audit-log', component: AuditLogComponent, ...withPermission([P.AUDIT_TRAIL_LIST, P.AUDIT_TRAIL_VIEW]) },
      {
        path: 'reports',
        component: ReportsComponent,
        ...withPermission([P.REPORT_LIST, P.REPORT_VIEW]),
      },
      {
        path: 'reports/:agentRef',
        component: ReportDetailComponent,
        ...withPermission([P.REPORT_VIEW, P.REPORT_LIST]),
      },

      {
        path: 'system-preferences/manage-permissions/:roleId',
        component: ManageRolePermissionsComponent,
        ...withPermission(
          [P.ROLE_MANAGE_PERMISSIONS, P.ROLE_UPDATE_PERMISSIONS],
          '/system-preferences',
        ),
      },
      {
        path: 'system-preferences',
        component: SystemPreferencesComponent,
        pathMatch: 'full',
        ...withPermission([
          P.ROLE_LIST,
          P.SIMULATE_AGENT_SETTINGS_LIST,
          P.SIMULATE_AGENT_SETTINGS_UPDATE,
          P.SMTP_SETTINGS_LIST,
          P.SMTP_SETTINGS_UPDATE,
          P.EMAIL_TEMPLATE_LIST,
          P.EMAIL_TEMPLATE_UPDATE,
          P.EMAIL_TEMPLATE_TOGGLE,
          P.ORGANIZATION_DETAILS_LIST,
          P.ORGANIZATION_DETAILS_UPDATE,
        ]),
      },
      { path: 'manage-roles/:roleId/permissions', redirectTo: 'system-preferences/manage-permissions/:roleId' },
      { path: 'manage-roles', redirectTo: 'system-preferences', pathMatch: 'full' },

      {
        path: 'system-preferences/email-template/:id',
        component: EditEmailTemplateComponent,
        ...withPermission(
          [P.EMAIL_TEMPLATE_LIST, P.EMAIL_TEMPLATE_UPDATE],
          '/system-preferences',
        ),
      },
      {
        path: 'user-setting/email-template/:id',
        redirectTo: 'system-preferences/email-template/:id',
      },
      {
        path: 'user-setting',
        component: UserSettingComponent,
      },
      { path: 'profile', component: ProfileComponent },
      { path: 'user-account-settings', component: UserAccountSettingsComponent },
      { path: 'mailbox', component: MailboxComponent },
      { path: 'calendar', component: CalendarComponent },
      { path: 'chat', component: ChatComponent },
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
    ],
  },
];
